import type { Assignment, RunSummary, ScheduleValidationIssue } from "../types";

/**
 * Joiner used everywhere in this module — a pipe character that cannot appear
 * inside a normalized identifier or course id (which are alphanumeric +
 * underscore + dash). Keeps tuple keys collision-free.
 */
const TUPLE_DELIM = "|";

/**
 * Builds the join key for an {@link Assignment} on the
 * `(normalizedStudentIdentifier, normalizedCourseId)` tuple — the same
 * composite-identity formula used by `computeSectionRoster` and the server-side
 * `Section.SectionKey`.
 */
function assignmentKey(a: Assignment): string {
  return `${a.normalizedStudentIdentifier}${TUPLE_DELIM}${a.normalizedCourseId}`;
}

/**
 * Builds the join key for a Stage 3 {@link ScheduleValidationIssue} on the
 * `(code, normalizedStudentIdentifier, courseId, sectionLabel)` tuple.
 * Defensively treats every nullable column as the empty string so a `null`
 * never matches a `null` by accident across runs.
 */
function issueKey(issue: ScheduleValidationIssue): string {
  return [
    issue.code,
    issue.normalizedStudentIdentifier ?? "",
    issue.courseId ?? "",
    issue.sectionLabel ?? "",
  ].join(TUPLE_DELIM);
}

/**
 * Counts `(normalizedStudentIdentifier, normalizedCourseId)` tuples that exist
 * in BOTH runs but with a DIFFERENT `sectionLabel` value. Null section labels
 * are treated as the empty string (matching the composite-section-identity
 * formula).
 *
 * Does NOT count students placed in only one run — those are surfaced by the
 * issue delta (Stage 3 emits `Student.RequestedButNotAssigned` or its inverse).
 *
 * Pure function — safe inside `useMemo`.
 */
export function computeAssignmentDelta(
  currentAssignments: Assignment[],
  priorAssignments: Assignment[],
): { studentsReassigned: number } {
  // Map prior assignments by composite key → section label for O(1) lookup.
  const priorByKey = new Map<string, string>();
  for (const a of priorAssignments) {
    priorByKey.set(assignmentKey(a), a.sectionLabel ?? "");
  }

  let studentsReassigned = 0;
  for (const current of currentAssignments) {
    const key = assignmentKey(current);
    const priorLabel = priorByKey.get(key);
    if (priorLabel === undefined) continue; // Not in both runs — skip.
    const currentLabel = current.sectionLabel ?? "";
    if (currentLabel !== priorLabel) {
      studentsReassigned += 1;
    }
  }
  return { studentsReassigned };
}

/**
 * Compares Error-severity issues by the tuple
 * `(code, normalizedStudentIdentifier, courseId, sectionLabel)` between current
 * and prior issue lists. Returns:
 *  - `conflictsResolved`: present in prior but not current
 *  - `newConflicts`:      present in current but not prior
 *
 * Filters internally to severity `Error` — Warnings are reflected in
 * {@link computeNetChange}, not here.
 *
 * Callers are expected to pass ONLY Stage 3 (`PostRun`) issues for the
 * relevant run; Stage 1 / Stage 2 issues are unrelated to run outcomes.
 */
export function computeIssueDelta(
  currentIssues: ScheduleValidationIssue[],
  priorIssues: ScheduleValidationIssue[],
): { conflictsResolved: number; newConflicts: number } {
  const currentErrorKeys = new Set<string>();
  for (const issue of currentIssues) {
    if (issue.severity !== "Error") continue;
    currentErrorKeys.add(issueKey(issue));
  }
  const priorErrorKeys = new Set<string>();
  for (const issue of priorIssues) {
    if (issue.severity !== "Error") continue;
    priorErrorKeys.add(issueKey(issue));
  }

  let conflictsResolved = 0;
  for (const key of priorErrorKeys) {
    if (!currentErrorKeys.has(key)) conflictsResolved += 1;
  }
  let newConflicts = 0;
  for (const key of currentErrorKeys) {
    if (!priorErrorKeys.has(key)) newConflicts += 1;
  }
  return { conflictsResolved, newConflicts };
}

/**
 * Net change in total issue count (errors + warnings) from prior → current.
 * Positive means MORE issues; negative means FEWER.
 */
export function computeNetChange(
  current: Pick<RunSummary, "errorIssueCount" | "warningIssueCount">,
  prior: Pick<RunSummary, "errorIssueCount" | "warningIssueCount">,
): number {
  const currentTotal = current.errorIssueCount + current.warningIssueCount;
  const priorTotal = prior.errorIssueCount + prior.warningIssueCount;
  return currentTotal - priorTotal;
}
